// @flow
import React from 'react'
import I18n from 'i18n-react'
import { humanizer } from 'devx-js-utilities'

import StringEnumRef from './StringEnumRef'

import { getValueForPath } from '../../FilterFunctions'

// components
import { SupplementaryInsuranceRow } from '../../../Components/index'

// types
import type { ResourcesType } from '../../Types/Resources'
import type { DefaultFormFieldState, DefaultFormFieldType } from './AbstractFormField'

type StringEnumRefProps = DefaultFormFieldType & {
  domain: {
    _type: string,
    values: Array<string>,
    resourceName: string,
  },
  options: any,
  resources: ResourcesType,
  value: {
    [key: string]: string,
  },
}

type SelectedValue = {
  [key: string]: string,
}

type StringEnumRefState = DefaultFormFieldState & {
  options?: Array<SelectedValue>,
  prevPropsValue: string,
  parentName: string,
}

export default class SupplementaryInsuranceSelect extends StringEnumRef<StringEnumRefProps, StringEnumRefState> {
  static getDerivedStateFromProps(nextProps: StringEnumRefProps, prevState: StringEnumRefState) {
    let changed = false
    const newState = {}
    if (nextProps.resources) {
      const resourcesValue = getValueForPath(nextProps.domain.resourceName, 0, nextProps.resources)
      const pricesDependantName = getValueForPath(
        nextProps.domain.resourceName.replace('limits', 'pricesDependantName'),
        0,
        nextProps.resources,
      )
      if (resourcesValue) {
        let priceValue = []
        if (pricesDependantName && nextProps.formData[pricesDependantName]) {
          priceValue = getValueForPath(
            `${nextProps.domain.resourceName.replace('limits', 'pricesDependent')}.${
              nextProps.formData[pricesDependantName]
            }`,
            0,
            nextProps.resources,
          )
        } else {
          priceValue = getValueForPath(
            nextProps.domain.resourceName.replace('limits', 'prices'),
            0,
            nextProps.resources,
          )
        }
        let parentName = nextProps.prefix.split('-')
        newState.parentName = parentName[parentName.length - 2]
        const valueNames = getValueForPath(['coverages', newState.parentName, 'valueNames'], 0, nextProps.resources)

        let labels = {}
        let options = []

        if (valueNames) {
          labels = resourcesValue.reduce((acc, value, index) => {
            const priceText = priceValue && priceValue[index] ? priceValue[index] : ''
            let label = valueNames[value]
              ? valueNames[value].replace(
                  `{value}`,
                  `${humanizer.csNumber(value)} ${I18n.translate('general.currency')}`,
                )
              : `${humanizer.csNumber(value)} ${I18n.translate('general.currency')}`
            acc[value] = label
            options.push({ value, label, priceText })
            return acc
          }, {})
        } else {
          labels = resourcesValue.reduce((acc, value, index) => {
            const priceText = priceValue && priceValue[index] ? priceValue[index] : ''
            acc[value] = `${humanizer.csNumber(value)} ${I18n.translate('general.currency')}`
            options.push({ value, label: acc[value], priceText })
            return acc
          }, {})
        }

        changed = true
        newState.labels = labels
        newState.value = null
        newState.options = options
        if (nextProps.value) {
          newState.value = { value: nextProps.value, label: labels[nextProps.value] }
        }
      }
    }
    if (nextProps.value !== prevState.prevPropsValue) {
      changed = true
      newState.prevPropsValue = nextProps.value
    }
    return changed ? newState : null
  }

  handleOnClick = (value: Object | string) => {
    this.props.onChange(value)
  }

  render() {
    const { id, prefix, textMode, value, resources } = this.props
    const { labels, options, parentName } = this.state

    if (textMode) {
      let label = value
      if (labels && labels[value]) {
        label = labels[value]
      }
      return <span>{label}</span>
    }

    const resourceContent = resources.coverages[parentName]
    const priceText =
      resourceContent && resourceContent.priceTranslationKey
        ? resourceContent.priceTranslationKey
        : 'general.pricePerYear'
    return (
      <div className="col-12">
        {options.map((option) => [
          <SupplementaryInsuranceRow
            id={`${prefix}${id}`}
            key={`${prefix}${id}-row`}
            label={option.label}
            onSelect={this.handleOnClick}
            priceText={option.priceText}
            priceTranslationKey={priceText}
            selected={`${value}` === `${option.value}`}
            value={option.value}
          />,
          <hr className="border-0 border-sm-top" key={`${prefix}${id}-hr`} />,
        ])}
      </div>
    )
  }
}
